import { Player } from '@lottiefiles/react-lottie-player'
import { motion, useAnimate, type AnimationSequence } from 'framer-motion'
import { useEffect, useState } from 'react'
import Marquee from 'react-fast-marquee'
import { useGlobalStore } from '../../../../store/global-store'
import type { NotifyRocketGameExploded } from '../../../../types/lives/notifies'

interface Props {
  toast: NotifyRocketGameExploded
}

// montar mensagem aqui
export function Toast({ toast }: Props) {
  const removeNotifyRocketGame = useGlobalStore((state) => {
    return state.removeNotifyRocketGame
  })

  const [scope, animate] = useAnimate()

  useEffect(() => {
    const sequence: AnimationSequence = [
      [scope.current, { x: 0 }, { duration: 2 }],
      [scope.current, { x: -window.innerWidth }, { delay: 5, duration: 2 }],
    ]

    if (toast?.id) {
      animate(sequence).then(() => {
        removeNotifyRocketGame(toast?.id as string)

        if ('ReactNativeWebView' in window) {
          // @ts-ignore
          window.ReactNativeWebView.postMessage('hide')
        }
      })
    }
  }, [toast, scope, animate, removeNotifyRocketGame])

  const hasTypeReward = 'typeReward' in toast

  // carrega o lottie SAME-ORIGIN (arquivos em /public) e passa o OBJETO pro Player
  // — evita o fetch cross-origin de storage-audio (que não renderiza na RN WebView).
  const [lottieData, setLottieData] = useState<unknown>(null)

  useEffect(() => {
    if (hasTypeReward) return
    let alive = true
    fetch(`/games/rocket-game/b${toast?.rocketGameId ?? 1}.json`)
      .then((r) => r.json())
      .then((d) => alive && setLottieData(d))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [toast?.rocketGameId, hasTypeReward])

  // cor do foguete usada só como ACENTO (borda/glow/halo) — não inunda o fundo
  const rocketColor =
    'imgRocketGame' in toast && typeof toast.imgRocketGame === 'string'
      ? toast.imgRocketGame
      : '#7c5cff'

  return (
    <motion.div
      ref={scope}
      className="font-bold h-14 w-96 max-w-[calc(100vw-24px)] flex flex-row rounded-2xl justify-start items-center pl-2 pr-3 gap-2 text-white"
      style={{
        x: window.innerWidth,
        background: hasTypeReward
          ? 'linear-gradient(95deg, #4568DC 0%, #3B4781 100%)'
          : 'linear-gradient(100deg, rgba(15,17,32,0.97) 0%, rgba(28,24,54,0.97) 60%, rgba(40,28,70,0.97) 100%)',
        border: `1.5px solid ${rocketColor}aa`,
        boxShadow: `0 8px 26px rgba(0,0,0,0.5), 0 0 16px ${rocketColor}55`,
      }}
    >
      {!hasTypeReward && lottieData && (
        <div className="relative shrink-0" style={{ width: 46, height: 46 }}>
          <div
            className="absolute rounded-full"
            style={{
              inset: -14,
              background: `radial-gradient(circle, ${rocketColor}80 0%, transparent 70%)`,
            }}
          />
          {/* lottie maior que o container -> transborda dando destaque */}
          <Player
            loop
            autoplay
            src={lottieData as object}
            style={{
              position: 'absolute',
              width: 76,
              height: 76,
              left: '50%',
              top: '50%',
              transform: 'translate(calc(-50% - 5px), calc(-50% - 5px))',
              filter: `drop-shadow(0 3px 9px ${rocketColor}aa)`,
            }}
          />
        </div>
      )}

      {hasTypeReward && (
        <img
          src={toast?.imgUser}
          className="w-9 h-9 bg-black rounded-full ring-2 ring-white/70 shrink-0"
        />
      )}

      <div className="min-w-0 w-full flex justify-start">
        <Marquee delay={1} speed={80} style={{ width: '80%' }}>
          <span
            style={{
              textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 0 8px rgba(0,0,0,0.5)',
              // folga entre as repetições do letreiro — sem isso o fim de uma
              // cópia emendava na próxima ("alone recebeualone recebeu")
              paddingRight: 48,
            }}
          >
            {toast?.message}
          </span>
        </Marquee>
      </div>

      {hasTypeReward && (
        <img
          src={(toast?.imgGiftFrame || toast?.imgGift) ?? toast?.imgItem}
          className="w-20 h-20 -mr-6 shrink-0"
          style={{ filter: 'drop-shadow(0 2px 7px rgba(0,0,0,0.55))' }}
        />
      )}
    </motion.div>
  )
}
